import { z } from 'zod'
import { CarrierGroup } from '../api/fetch-carrier-api'

export const CrawlScopeSchema = z.object({
  broadScan: z.boolean(),
  standardScan: z.boolean(),
  deepScan: z.boolean(),
  testScan: z.boolean().optional(),
})

// Same pattern used elsewhere in the app for email-list validation
// (see src/enrollment-platform/pages/finance/components/helpers.ts).
export const NOTIFICATION_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidNotificationEmail(value: string): boolean {
  return NOTIFICATION_EMAIL_REGEX.test(value)
}

/** Returns the subset of `emails` that are not valid email addresses. */
export function getInvalidNotificationEmails(emails: string[]): string[] {
  return emails.filter((email) => !isValidNotificationEmail(email))
}

export const CarrierUpsertSchema = z.object({
  year: z
    .number({ required_error: 'Plan year is required' })
    .int()
    .min(2020, 'Year must be 2020 or later')
    .max(2030, 'Year must be 2030 or earlier'),
  groupCode: z.nativeEnum(CarrierGroup, {
    required_error: 'Carrier group is required',
  }),
  baseUrl: z
    .string()
    .min(1, 'Base URL is required')
    .url('Please enter a valid URL'),
  autoSchedule: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true),
  remark: z.string().optional(),
  crawlScope: CrawlScopeSchema.optional().default({
    broadScan: true,
    standardScan: false,
    deepScan: false,
  }),
  notificationEmails: z
    .array(z.string().email('Please enter a valid email address'))
    .optional()
    .default([]),
})

export type CarrierUpsertFormData = z.infer<typeof CarrierUpsertSchema>

// UI scan mode: maps the Fetch Coverage dropdown options to the crawlScope booleans
export type ScanMode = 'test' | 'quick' | 'full'

export const SCAN_MODE_TO_CRAWL_SCOPE: Record<
  ScanMode,
  z.infer<typeof CrawlScopeSchema>
> = {
  test: {
    broadScan: true,
    standardScan: false,
    deepScan: false,
    testScan: true,
  },
  quick: {
    broadScan: true,
    standardScan: false,
    deepScan: false,
    testScan: false,
  },
  full: {
    broadScan: true,
    standardScan: true,
    deepScan: true,
    testScan: false,
  },
}

export function crawlScopeToScanMode(
  scope?: z.infer<typeof CrawlScopeSchema> | null
): ScanMode {
  if (scope?.testScan) return 'test'
  return scope?.deepScan ? 'full' : 'quick'
}
